import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { isColorBright, RGB_2_HEX } from '../../lib/ep';
import { Stage, Layer, Group, Rect, Text, Circle, Path, Line } from 'react-konva';
import Konva from 'konva';
import { localizeFields } from '../builder/DynamicFields';

const standard_board_size = 600;	// px
const standard_button_size = 200;	// px
const start_tutorial_delay = 3;		// seconds

const DefaultInterationIconBG = ({ size, ...props }) => {
	const bg_radius = size / 2;
	const factor = 190 / 200;
	const inner_radius = bg_radius * factor;
	const scale = size / standard_button_size;
	const dash = 8.53 * scale;

	const bg_color = props?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const color = props?.color ?? 'rgba(0, 0, 0, 1)';

	return (
		<>
			<Circle
				radius={bg_radius}
				fill={bg_color}
				x={bg_radius}
				y={bg_radius}
			/>

			<Circle
				radius={inner_radius}
				fill='transparent'
				x={inner_radius + (1 - factor) * bg_radius}
				y={inner_radius + (1 - factor) * bg_radius}
				stroke={color}
				strokeWidth={Math.max(1, 2 * scale)}
				dash={[dash, dash]}
			/>
		</>
	)
};

export const DefaultInterationIcon = ({ type, size, ...props }) => {
	const scale = size / standard_button_size;

	const bg_color = props?.color ?? 'rgba(255, 255, 255, 1)';
	const color = (isColorBright(RGB_2_HEX(bg_color))) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)';

	return (
		<>
			<DefaultInterationIconBG
				size={size}
				bg_color={bg_color}
				color={color}
			/>

			{(type == 'like') && (
				<Path
					data="M0.01,50c0,0,57.49-31.91,57.49-67.62C57.5-36.42,45.29-50,29.19-50C13.99-50,3.4-42.9,0.01-31.45C-3.4-42.9-13.99-50-29.2-50c-16.08,0-28.3,13.58-28.3,32.38C-57.5,18.09,0.01,50,0.01,50z"
					scaleX={scale}
					scaleY={scale}
					x={scale * standard_button_size / 2}
					y={scale * standard_button_size / 2}
					fill={color}
				/>
			)}

			{(type == 'dislike') && (
				<Line
					points={[50,-29.8, 29.8,-50, 0,-20.2, -29.8,-50, -50,-29.79, -20.2,0, -50,29.8, -29.79,50, 0,20.21, 29.8,50, 50,29.8, 20.2,0]}
					scaleX={scale}
					scaleY={scale}
					x={scale * standard_button_size / 2}
					y={scale * standard_button_size / 2}
					fill={color}
					closed={true}
				/>
			)}
		</>
	)
};

const LikeDislikeCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const is_test = props?.is_test ?? false;

	const initial_players = localizeFields(stage?.likedislike_players ?? [],props.locale);
	const players = [...initial_players].reverse();
	const total_players = initial_players.length;

	const bg_image = props.indexed_images?.bg_image ?? false;

	const shittyIOS = true;	// set to false to disable shadows and some effects

	const bg_color = stage?.bg_color ?? 'rgba(255, 255, 255, 1)';
	const text_color = stage?.text_color ?? 'rgba(0, 0, 0, 1)';
	const text_border_width = parseInt(stage?.likedislike_text_border_width ?? '0');
	const like_color = stage?.likedislike_like_color ?? 'rgba(120, 195, 0, 1)';
	const dislike_color = stage?.likedislike_dislike_color ?? 'rgba(215, 35, 60, 1)';
	const card_border_color = stage?.likedislike_border_color ?? 'rgba(0, 0, 0, 1)';
	const card_border_width = parseInt(stage?.likedislike_border_width ?? 1);
	const card_border_radius = parseInt(stage?.likedislike_border_radius ?? 30);
	const display_outter_border = stage?.display_outter_border ?? true;
	const card_bg_color = stage?.likedislike_card_bg_color ?? bg_color;
	const display_player_text = stage?.likedislike_display_player_text ?? true;

	const opacity_steps = 3;
	const max_cards_displayed = 8;
	const card_size_scale = 1.6;

	const refs = useRef([]);
	const size = useRef(standard_board_size);
	const in_transition = useRef(false);
	const is_dragging = useRef(false);
	const is_playable = useRef(false);
	const answers = useRef([]);
	const actual_player = useRef(players.length - 1);
	const done_players = useRef(0);
	const start_time = useRef(false);
	const already_mounted = useRef(false);
	const board_scale = useRef(size.current / standard_board_size);
	const card_v_separation = useRef(15 * card_size_scale * board_scale.current);
	const player_text_w = useRef(size.current - 4 * card_v_separation.current);
	const player_w = useRef(card_size_scale * size.current / 3);
	const player_h = useRef(player_w.current * 5 / 4);
	const actual_player_y = useRef(size.current / 6 + card_size_scale * card_v_separation.current / 2);
	const tutorial_timer = useRef(null);
	const execute_tutorial = useRef(true);

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const changePreviewPlayer = (preview_player_id) => {
		if(!is_builder || is_test) return;
		if(refs.current['layer'] == undefined) return;

		const image_data = props?.indexed_images[`player_${preview_player_id}`] ?? false;
		const image_scale = (image_data !== false) ? Math.min((player_w.current / image_data.image_w), (player_h.current / image_data.image_h)) : 1;

		refs.current[`player_${total_players - 1}_img`].setAttrs({
			fillPatternImage:image_data?.fillPatternImage ?? null,
			fillPatternScaleX:image_scale,
			fillPatternScaleY:image_scale,
		});

		refs.current[`player_${total_players - 1}_img`].cache(default_cache_props);

		if(display_player_text) {
			refs.current['player_text'].setAttrs({
				text:setNewCardTitle(),
			});

			if(text_border_width > 0) {
				refs.current['player_text_shadow'].setAttrs({
					text:setNewCardTitle(),
				});
			}

			refs.current['player_text_all'].cache(default_cache_props);
		}

		refs.current['layer'].draw();
	};

	const gameReset = (reset_player, preview_player_id = null) => {
		if(!is_builder || is_test) is_playable.current = true;
		done_players.current = 0;

		if(players.length == 0) return;

		if(reset_player === true) changePreviewPlayer(preview_player_id);
	};

	const generateCache = () => {
		if(refs.current['layer'] == undefined) return;

		refs.current['bg'].cache(default_cache_props);

		refs.current['icon_dislike'].cache(default_cache_props);
		refs.current['icon_like'].cache(default_cache_props);

		players.map((foo, p) => {
			refs.current[`player_${p}_img`].cache(default_cache_props);
			if(shittyIOS) refs.current[`player_${p}_like`].cache(default_cache_props);
			if(shittyIOS) refs.current[`player_${p}_dislike`].cache(default_cache_props);
			if(shittyIOS) refs.current[`player_${p}_shadow`].cache({ ...default_cache_props, offset:30 });
		})

		if(shittyIOS) refs.current['icon_dislike_shadow'].cache({ ...default_cache_props, offset:30 });
		if(shittyIOS) refs.current['icon_like_shadow'].cache({ ...default_cache_props, offset:30 });
		if(display_player_text) refs.current['player_text_all'].cache(default_cache_props);
	};

	const gameInit = (reset_player, preview_player_id = null) => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current = size_received;
				board_scale.current = size.current / standard_board_size;
				card_v_separation.current = 15 * card_size_scale * board_scale.current;
				player_text_w.current = size.current - 4 * card_v_separation.current;
				player_w.current = card_size_scale * size.current / 3;
				player_h.current = player_w.current * 5 / 4;
				actual_player_y.current = size.current / 6 + card_size_scale * card_v_separation.current / 2;

				gameReset(reset_player, preview_player_id);

				setFullLoaded(true);

				if(is_builder && !is_test) {
					setTimeout(() => {
						generateCache();
						mountBoardInstant();
					}, 200);
					return;
				}

				setTimeout(() => {
					generateCache();
					mountBoard();
				}, 500);
			});
		} else {
			setFullLoaded(true);
		}
	};

	useEffect(() => {
		if(props.loaded_status != 'LOADED') return;

		gameInit();
		generateCache();
	}, [
		props.loaded_status,
		props.indexed_images,
		stage.likedislike_players_mode,
	]);

	useEffect(() => {
		if(props.loaded_status != 'LOADED') return;

		generateCache();
	}, [
		stage.bg_color,
		stage.likedislike_border_radius,
		stage.likedislike_like_color,
		stage.likedislike_dislike_color,
	]);

	useEffect(() => {
		let player_index = 0;
		initial_players.map((player, p) => {
			if(player.id != props.default_player_index) return;
			player_index = p;
		});

		if(actual_player.current - (players.length - (player_index + 1)) == 0) return;
		actual_player.current = players.length - (player_index + 1);

		gameInit(true, props.default_player_index);
	}, [
		props.default_player_index,
	]);

	useImperativeHandle(ref, () => ({
	}), []);

	const calcDisplacementPercent = () => {
		if(refs.current[`player_${actual_player.current}`] == undefined) return 0;
		const displacement = refs.current[`player_${actual_player.current}`].attrs.x - size.current / 2;
		return Math.max(-1, Math.min(1, displacement / (icons_size + 5 * card_v_separation.current)));
	};

	const resetCard = () => {
		if(shittyIOS && refs.current[`player_${actual_player.current}_like`] != undefined) {
			refs.current[`player_${actual_player.current}_like`].to({
				opacity:0,
				duration:0.5,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current[`player_${actual_player.current}_dislike`].to({
				opacity:0,
				duration:0.5,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current[`player_${actual_player.current}_shadow`].to({
				y:player_h.current + 10,
				rotation:0,
				duration:0.5,
				easing:Konva.Easings.BounceEaseOut,
			});
		}

		if(refs.current[`player_${actual_player.current}`] != undefined) {
			refs.current[`player_${actual_player.current}`].to({
				rotation:0,
				x:size.current / 2,
				y:refs.current[`player_${actual_player.current}`].attrs.initial_y,
				duration:0.5,
				easing:Konva.Easings.BounceEaseOut,
				onFinish:() => {
					is_dragging.current = false;
				},
			});
		}

		autoResetButtons();
	};

	const autoResetButtons = () => {
		if(refs.current['icon_dislike'] == undefined) return;

		refs.current['icon_dislike'].to({
			x:refs.current['icon_dislike'].attrs.initial_x,
			scaleX:1,
			scaleY:1,
			rotation:0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['icon_like'].to({
			x:refs.current['icon_like'].attrs.initial_x,
			scaleX:1,
			scaleY:1,
			rotation:0,
			duration:0.5,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const finishGame = () => {
		if(refs.current['icon_dislike'] == undefined) return;

		is_playable.current = false;

		if(shittyIOS && refs.current['icon_dislike_shadow'] != undefined) {
			refs.current['icon_dislike_shadow'].to({
				x:-1 * (icons_size + card_v_separation.current) + icons_size / 2,
				duration:0.7,
				easing:Konva.Easings.StrongEaseInOut,
			});

			refs.current['icon_like_shadow'].cache({
				x:size.current + (icons_size + card_v_separation.current) + icons_size / 2,
				duration:0.7,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		refs.current['icon_dislike'].to({
			x:-1 * (icons_size + card_v_separation.current) + icons_size / 2,
			rotation:-200,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['icon_like'].to({
			x:size.current + (icons_size + card_v_separation.current) + icons_size / 2,
			rotation:200,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(is_builder && !is_test) {
					already_mounted.current = false;
					mountBoardInstant();
					return;
				}

				props.onEnded(true, { likedislike:answers.current });
			},
		});
	};

	const getCardY = (index) => {
		const displacement_y = (total_players - max_cards_displayed) * card_v_separation.current;

		const final_y_min = (total_players - max_cards_displayed - 1) * card_v_separation.current + actual_player_y.current - displacement_y;
		const final_y = (index + done_players.current) * card_v_separation.current + actual_player_y.current - displacement_y;
		return Math.max(final_y, final_y_min);
	};

	const generateCardStaticProps = (index) => {
		return {
			initial_y:getCardY(index),
			draggable:((!is_builder || is_test) && actual_player.current == index) ? true : false,
		}
	};

	const generateCardMovingProps = (index) => {
		const effective_index = players.length - index - 1;
		const scale = Math.max(0, (1 - effective_index * opacity_steps * 0.03));

		if(effective_index > max_cards_displayed) {
			return {
				opacity:0,
				y:getCardY(index),
				scaleX:0.1,
				scaleY:0.1,
			}
		}

		return {
			opacity:Math.max(0.1, 1 - 1 / opacity_steps * effective_index),
			y:getCardY(index),
			scaleX:Math.max(0.1, scale),
			scaleY:Math.max(0.1, scale),
		};
	};

	const setNewCardTitle = () => {
		if(actual_player.current < 0) return "";	// in case the cards are passed very quickly
		return players[actual_player.current]?.name ?? `Option 1`;
	};

	const moveToNextCard = () => {
		in_transition.current = true;

		actual_player.current--;
		done_players.current++;

		if(actual_player.current < 0) {
			setTimeout(() => {
				finishGame();
			}, 1000);
			return;
		}

		players.splice(-1, 1);

		let finished = 0;
		players.map((player, p) => {
			refs.current[`player_${p}`].setAttrs(generateCardStaticProps(p));

			const card_dynamic_props = generateCardMovingProps(p);

			refs.current[`player_${p}_cont`].to({
				opacity:card_dynamic_props.opacity,
				duration:0.5,
				easing:Konva.Easings.BounceEaseOut,
			});

			refs.current[`player_${p}`].to({
				...card_dynamic_props,
				opacity:1,// we overwrite the opacity value because it'll be done in `player_${p}_cont` item
				duration:0.5,
				easing:Konva.Easings.BounceEaseOut,
				onFinish:() => {
					finished++;

					if(finished == players.length) {
						in_transition.current = false;

						if(!is_builder || is_test) {
							start_time.current = Date.now();
						}
						
						if(display_player_text) {
							refs.current['player_text'].setAttrs({
								text:setNewCardTitle(),
							});

							if(text_border_width > 0) {
								refs.current['player_text_shadow'].setAttrs({
									text:setNewCardTitle(),
								});
							}

							refs.current['player_text_all'].cache(default_cache_props);

							refs.current['player_text_all'].to({
								opacity:1,
								duration:0.3,
								easing:Konva.Easings.StrongEaseInOut,
								onFinish:() => {
								},
							});
						}
					}
				},
			});
		});
	};

	const addVote = (direction = 1) => {
		const end_time = Date.now();

		const voted_player = initial_players[total_players - actual_player.current - 1];
		answers.current.push({
			id:voted_player.id,
			type:(direction > 0) ? 'like' : 'dislike',
			time:(!is_builder || is_test) ? (end_time - start_time.current) / 1000 : 0,
		});
	};

	const answerCard = (direction = 1, automatic = false) => {
		if(is_playable.current === false) return;

		execute_tutorial.current = false;

		const final_coord_x = card_size_scale * size.current / 4;

		addVote(direction);

		const moving_card = actual_player.current;
		setTimeout(() => {
			moveToNextCard();
		}, 50);

		if(display_player_text) {
			refs.current['player_text_all'].to({
				opacity:0,
				duration:0.3,
				easing:Konva.Easings.StrongEaseInOut,
			});
		}

		if(shittyIOS) {
			refs.current[`player_${moving_card}_shadow`].to({
				x:player_w.current / 2 + direction * card_size_scale * player_w.current / 2,
				y:player_h.current / 4,
				rotation:-direction * 90,
				opacity:0,
				duration:(automatic) ? 1 : 0.6,
				easing:Konva.Easings.StrongEaseOut,
			});
		}

		refs.current[`player_${moving_card}`].to({
			x:(direction > 0) ? size.current + final_coord_x : -final_coord_x,
			y:refs.current[`player_${moving_card}`].attrs.initial_y,
			rotation:direction * 90,
			duration:(automatic) ? 1 : 0.6,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				is_dragging.current = false;
			},
		});

		if(automatic === false) autoResetButtons();
	};

	const dragMove = () => {
		if(is_playable.current === false) return;

		const displacement_percent = calcDisplacementPercent();

		const pre_option = (displacement_percent > 0) ? 'like' : 'dislike';

		if(shittyIOS) {
			refs.current[`player_${actual_player.current}_${pre_option}`].setAttrs({
				opacity:Math.min(0.6, 0.6 * Math.abs(displacement_percent)),
			});
		}

		const card = refs.current[`player_${actual_player.current}`];

		card.setAttrs({
			rotation:displacement_percent * 18,
			x:Math.min(size.current - 2 * card_v_separation.current, Math.max(2 * card_v_separation.current, card.attrs.x)),
			y:card.attrs.initial_y,
		});

		if(shittyIOS) {
			refs.current[`player_${actual_player.current}_shadow`].setAttrs({
				rotation:-displacement_percent * 18,
				y:player_h.current + 10 + Math.abs(displacement_percent * 8),
				opacity:0.3 - 0.2 * Math.abs(displacement_percent),
			});
		}

		refs.current[`icon_${pre_option}`].setAttrs({
			x:refs.current[`icon_${pre_option}`].attrs.initial_x + 2 * card_v_separation.current * displacement_percent,
			scaleX:1 + 0.5 * Math.abs(displacement_percent),
			scaleY:1 + 0.5 * Math.abs(displacement_percent),
			rotation:15 * displacement_percent,
		});
	};

	const dragEnd = () => {
		const displacement_percent = calcDisplacementPercent();

		if(Math.abs(displacement_percent) < 0.7) {
			resetCard();
			return;
		}

		answerCard((displacement_percent > 0) ? 1 : -1);
	};

	const playTutorial = () => {
		if(tutorial_timer.current != undefined) return;
		if(refs.current[`player_${actual_player.current}`] == undefined) {
			if(tutorial_timer.current != undefined) clearInterval(tutorial_timer.current);
			return;
		}

		const max_displacement = 0.3; // in %
		const distance = max_displacement * size.current; // in px
		const speed = 3; // in steps (the bigger the number, the slower the animation will be)
		const num_loops_per_round = 2;

		const card = refs.current[`player_${actual_player.current}`];
		const initial_x = card.attrs.x;

		let i = 0;
		let loop = 0;
		let direction = 1;
		tutorial_timer.current = setInterval(() => {
			if(execute_tutorial.current === false || loop == num_loops_per_round) {
				clearInterval(tutorial_timer.current);
				tutorial_timer.current = null;

				if(execute_tutorial.current !== false && loop == num_loops_per_round) {
					resetCard();

					setTimeout(() => {
						playTutorial();
					}, start_tutorial_delay * 1000);
				}

				return;
			}

			const m = Math.sin(i / speed);
			const actual_direction = (m > 0) ? 1 : -1;
			const percent = max_displacement * m;
			if(direction != actual_direction) {
				direction = actual_direction;
				if(direction > 0) loop++;
			}

			i++;
			card.setAttrs({
				x:initial_x + distance * percent,
			});

			dragMove();
			refs.current['layer'].draw();
		}, (1000 / 15));
	};

	const mountBoardInstant = () => {
		if(already_mounted.current) return;
		if(refs.current['layer'] == undefined) return;

		players.map((foo, p) => {
			const card_dynamic_props = generateCardMovingProps(p);

			if(refs.current[`player_${p}`] == undefined) return;

			refs.current[`player_${p}`].setAttrs({
				...card_dynamic_props,
				y:refs.current[`player_${p}`].attrs.initial_y,
				opacity:1,
			});
		});

		refs.current['layer'].draw();
	};

	const mountBoard = () => {
		if(already_mounted.current) return;
		if(refs.current['layer'] == undefined) return;

		refs.current['icon_dislike'].to({
			x:refs.current['icon_dislike'].attrs.initial_x,
			rotation:0,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
		});

		refs.current['icon_like'].to({
			x:refs.current['icon_like'].attrs.initial_x,
			rotation:0,
			duration:0.7,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(shittyIOS && refs.current['icon_like_shadow'] != undefined) {
					refs.current['icon_like_shadow'].setAttrs({
						x:refs.current['icon_like'].attrs.initial_x,
						luminance:-10,
						contrast:100,
					});

					refs.current['icon_dislike_shadow'].setAttrs({
						x:card_v_separation.current + icons_size / 2,
						luminance:-10,
						contrast:100,
					});
				}

				setTimeout(() => {
					playTutorial();
				}, start_tutorial_delay * 1000);
			},
		});

		refs.current['layer'].draw();

		let done_players = Math.min(total_players, max_cards_displayed);

		players.map((foo, p) => {
			const card_dynamic_props = generateCardMovingProps(p);

			setTimeout(() => {
				if(refs.current[`player_${p}`] == undefined) return;

				refs.current[`player_${p}`].to({
					...card_dynamic_props,
					y:refs.current[`player_${p}`].attrs.initial_y,
					opacity:1,
					duration:(p == total_players - 1) ? 1 : 0.7,
					easing:Konva.Easings.StrongEaseInOut,
					onFinish:() => {
						done_players--;

						if(done_players == 0) {
							start_time.current = Date.now();
							already_mounted.current = true;
							is_playable.current = true;
						}
					},
				});
			}, ((p == total_players - 1) ? 100 : ((total_players - p) * 100 + 600)));
		});
	};

	const hoverIcon = (direction) => {
		if((window?.platform ?? 'desktop') != 'desktop') return;
		if(is_dragging.current) return;
		if(is_playable.current === false) return;

		const item = (direction > 0) ? 'icon_like' : 'icon_dislike';
		refs.current[item].to({
			scaleX:1.15,
			scaleY:1.15,
			y:icons_size / 2 - card_v_separation.current,
			rotation:direction * 10,
			duration:0.3,
			easing:Konva.Easings.BounceEaseOut,
		});

		if(shittyIOS) {
			refs.current[`${item}_shadow`].to({
				opacity:0.3,
				duration:0.3,
				easing:Konva.Easings.BounceEaseOut,
			});
		}
	};

	const houtIcon = (direction) => {
		if((window?.platform ?? 'desktop') != 'desktop') return;
		if(is_dragging.current) return;
		if(is_playable.current === false) return;

		const item = (direction > 0) ? 'icon_like' : 'icon_dislike';
		refs.current[item].to({
			scaleX:1,
			scaleY:1,
			y:icons_size / 2,
			rotation:0,
			duration:0.3,
			easing:Konva.Easings.BounceEaseOut,
		});

		if(shittyIOS) {
			refs.current[`${item}_shadow`].to({
				opacity:0,
				duration:0.3,
				easing:Konva.Easings.BounceEaseOut,
			});
		}
	};

	const text_props = {
		width:player_text_w.current,
		offsetX:player_text_w.current / 2,
		align:'center',
		verticalAlign:'top',
		fontSize:37 * board_scale.current,
		fill:text_color,
	};

	const card_props = {
		width:player_w.current,
		height:player_h.current,
		cornerRadius:card_border_radius,
	};

	const icons_size = size.current / 6;

	const like_icon_image = props?.indexed_images['like_icon'] ?? false;
	const like_icon_image_scale = (like_icon_image !== false) ? (icons_size / like_icon_image.image_w) : 1;

	const dislike_icon_image = props?.indexed_images['dislike_icon'] ?? false;
	const dislike_icon_image_scale = (dislike_icon_image !== false) ? (icons_size / dislike_icon_image.image_w) : 1;

	if(!full_loaded) return null;

	const main_classes = ['canvas_cont'];
	if(display_outter_border === false) main_classes.push('not_bordered');

	const bg_image_scale = (bg_image !== false) ? (size.current / Math.min((bg_image?.image_w ?? 600), (bg_image?.image_h ?? 600))) : 1;

	let text_player_color_rev = 'rgba(255, 255, 255, 1)';
	if(isColorBright(RGB_2_HEX(text_color))) {
		text_player_color_rev = 'rgba(0, 0, 0, 1)';
	}

	const hover_area_factor = 1.6;

	const dislike_icon_initial_x = card_v_separation.current + icons_size / 2 - 5;
	const dislike_icon_x = (!is_builder || is_test) ? (-1 * (icons_size + card_v_separation.current) + icons_size / 2) : dislike_icon_initial_x;

	const like_icon_initial_x = size.current - icons_size - card_v_separation.current + icons_size / 2 + 5;
	const like_icon_x = (!is_builder || is_test) ? (size.current + (icons_size + card_v_separation.current) + icons_size / 2) : like_icon_initial_x;

	return (
		<Stage
			className={main_classes.join(" ")}
			width={size.current}
			height={size.current}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					fill={bg_color}
					width={size.current}
					height={size.current}
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="no-repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
				/>

				{/* ACTIVE ZONES - ini */}
				<Group
					y={(size.current - icons_size) / 2}
				>
					{(shittyIOS) && (
						<Circle
							ref={(node) => { refs.current['icon_dislike_shadow'] = node; }}
							radius={icons_size / 2}
							x={-1 * (icons_size + card_v_separation.current) + icons_size / 2}
							y={icons_size * 0.95}
							opacity={0}
							scaleX={0.9}
							scaleY={0.2}
							fill='black'
							blurRadius={80}
							filters={[Konva.Filters.Blur]}
						/>
					)}

					{(shittyIOS) && (
						<Circle
							ref={(node) => { refs.current['icon_like_shadow'] = node; }}
							radius={icons_size / 2}
							x={size.current + (icons_size + card_v_separation.current) + icons_size / 2}
							y={icons_size * 0.95}
							opacity={0}
							scaleX={0.9}
							scaleY={0.2}
							fill='black'
							blurRadius={80}
							filters={[Konva.Filters.Blur]}
						/>
					)}

					<Group
						ref={(node) => { refs.current['icon_dislike'] = node; }}
						width={icons_size}
						height={icons_size}
						x={dislike_icon_x}
						y={icons_size / 2}
						initial_x={dislike_icon_initial_x}
						rotation={(!is_builder || is_test) ? -200 : 0}
						offsetX={icons_size / 2}
						offsetY={icons_size / 2}
					>
						{(dislike_icon_image !== false) ? (
							<Rect
								width={icons_size}
								height={icons_size}
								fillPatternImage={dislike_icon_image.fillPatternImage}
								fillPatternRepeat="no-repeat"
								fillPatternScaleX={dislike_icon_image_scale}
								fillPatternScaleY={dislike_icon_image_scale}
							/>
						) : (
							<DefaultInterationIcon
								type='dislike'
								size={icons_size}
								color={dislike_color}
							/>
						)}
					</Group>

					<Group
						ref={(node) => { refs.current['icon_like'] = node; }}
						width={icons_size}
						height={icons_size}
						x={like_icon_x}
						y={icons_size / 2}
						initial_x={like_icon_initial_x}
						rotation={(!is_builder || is_test) ? 200 : 0}
						offsetX={icons_size / 2}
						offsetY={icons_size / 2}
					>
						{(like_icon_image !== false) ? (
							<Rect
								width={icons_size}
								height={icons_size}
								fillPatternImage={like_icon_image.fillPatternImage}
								fillPatternRepeat="no-repeat"
								fillPatternScaleX={like_icon_image_scale}
								fillPatternScaleY={like_icon_image_scale}
							/>
						) : (
							<DefaultInterationIcon
								type='like'
								size={icons_size}
								color={like_color}
							/>
						)}
					</Group>
				</Group>
				{/* ACTIVE ZONES - end */}

				{/* PLAYER TEXTS - ini */}
				{(display_player_text) && (
					<Group
						ref={(node) => { refs.current['player_text_all'] = node; }}
						x={size.current / 2}
						y={4 * size.current / 5 + card_size_scale * 27}
					>
						{(text_border_width > 0) && (
							<Text
								ref={(node) => { refs.current['player_text_shadow'] = node; }}
								text={setNewCardTitle()}
								{...text_props}
								lineCap='round'
								lineJoin='round'
								strokeWidth={text_border_width}
								stroke={text_player_color_rev}
								fill={text_player_color_rev}
							/>
						)}

						<Text
							ref={(node) => { refs.current['player_text'] = node; }}
							text={setNewCardTitle()}
							{...text_props}
						/>
					</Group>
				)}
				{/* PLAYER TEXTS - end */}

				{players.map((player, p) => {
					const bg_image = props?.indexed_images[`player_${player.id}`] ?? false;
					const bg_image_scale = (bg_image !== false) ? Math.min((player_w.current / bg_image.image_w), (player_h.current / bg_image.image_h)) : 1;

					const card_dynamic_props = generateCardMovingProps(p);

					return (
						<Group
							key={`player_${p}`}
							ref={(node) => { refs.current[`player_${p}`] = node; }}
							{...generateCardStaticProps(p)}
							y={(p == total_players - 1) ? (size.current + card_props.height + 10) : size.current / 2}
							scaleX={card_dynamic_props.scaleX * ((p == total_players - 1) ? 1 : 0.9)}
							scaleY={card_dynamic_props.scaleY * ((p == total_players - 1) ? 1 : 0)}
							opacity={1}	// we overwrite the opacity value because it'll be done in `player_${p}_cont` item
							offsetX={player_w.current / 2}
							offsetY={player_h.current / 2}
							x={size.current / 2}
							onDragStart={() => {
								is_dragging.current = true;
								execute_tutorial.current = false;
							}}
							onDragMove={() => {
								dragMove();
							}}
							onDragEnd={() => {
								dragEnd();
							}}
							rotation={0}
						>
							{/* This element prevents cards from behind to be partially displayed through the semi-opaque stacked card on top */}
							<Rect
								{...card_props}
								fill={card_bg_color}
							/>

							{(shittyIOS) && (
								<Circle
									ref={(node) => { refs.current[`player_${p}_shadow`] = node; }}
									x={player_w.current / 2}
									y={player_h.current + 10}
									radius={player_w.current / 2}
									scaleX={0.8}
									scaleY={0.1}
									offsetY={2}
									opacity={0.3}
									fill='black'
									blurRadius={120}
									filters={[Konva.Filters.Blur]}
								/>
							)}

							<Group
								ref={(node) => { refs.current[`player_${p}_cont`] = node; }}
								opacity={card_dynamic_props.opacity}
							>
								<Rect
									ref={(node) => { refs.current[`player_${p}_img`] = node; }}
									{...card_props}
									fill={player?.color ?? 'rgba(160, 160, 160, 1)'}
									fillPatternImage={bg_image.fillPatternImage}
									fillPatternScaleX={bg_image_scale}
									fillPatternScaleY={bg_image_scale}
									fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
								/>

								{(shittyIOS) && (
									<Group
										ref={(node) => { refs.current[`player_${p}_like`] = node; }}
										opacity={0}
									>
										<Rect
											{...card_props}
											fill={like_color}
											globalCompositeOperation='multiply'
											opacity={0.5}
										/>

										<Rect
											{...card_props}
											fill={like_color}
											globalCompositeOperation='screen'
										/>
									</Group>
								)}

								{(shittyIOS) && (
									<Group
										ref={(node) => { refs.current[`player_${p}_dislike`] = node; }}
										opacity={0}
									>
										<Rect
											{...card_props}
											fill={dislike_color}
											globalCompositeOperation='multiply'
											opacity={0.5}
										/>

										<Rect
											{...card_props}
											fill={dislike_color}
											globalCompositeOperation='screen'
										/>
									</Group>
								)}

								<Rect
									{...card_props}
									strokeWidth={card_border_width}
									stroke={card_border_color}
									fill='transparent'
								/>
							</Group>
						</Group>
					)
				})}

				{/* ACTIVE ZONES HOVER - ini */}
				<Group
					ref={(node) => { refs.current['icons_hover'] = node; }}
					y={(size.current - hover_area_factor * icons_size) / 2}
				>
					<Rect
						width={hover_area_factor * icons_size}
						height={hover_area_factor * icons_size}
						x={card_v_separation.current / hover_area_factor}
						opacity={0}
						fill='white'
						onClick={() => answerCard(-1, true)}
						onTap={() => answerCard(-1, true)}
						onMouseEnter={() => hoverIcon(-1)}
						onMouseLeave={() => houtIcon(-1)}
					/>

					<Rect
						width={hover_area_factor * icons_size}
						height={hover_area_factor * icons_size}
						x={size.current - hover_area_factor * icons_size - card_v_separation.current / hover_area_factor}
						opacity={0}
						fill='white'
						onClick={() => answerCard(1, true)}
						onTap={() => answerCard(1, true)}
						onMouseEnter={() => hoverIcon(1)}
						onMouseLeave={() => houtIcon(1)}
					/>
				</Group>
				{/* ACTIVE ZONES HOVER - end */}
			</Layer>
		</Stage>
	)
})

export default LikeDislikeCanvas;